import React, { useState } from 'react';
import { useApp } from '../App';
import { createEvent, patchEvent, fetchEvents } from '../lib/gcal';
import { fsSet, fsLogChange } from '../lib/firebase';
import { toLocalDateStr, fmtClockTime } from '../lib/datetime';
import { buildFOHDescription } from '../api/assignments';
import { SESSION_STATUS, calculateSessionPay, resolveActivityTypes } from '../config/appConfig';

// ─── SCHEDULE CAREGIVER ─────────────────────────────────────────────────────────
// Books a caregiver — one-off or recurring — writing both a Google Calendar
// event (so the family sees it on their normal calendar) and a
// families/{id}/sessions doc (so it shows up in the caregiver's own
// tokenized "My Sessions" list). See docs/architecture.md and the caregiver
// booking overhaul plan for the full data-model rationale.

const RECUR_DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const QUICK_TIMES = [
  { label: '3:00', value: '15:00' },
  { label: '4:00', value: '16:00' },
  { label: '5:00', value: '17:00' },
  { label: '6:00', value: '18:00' },
];
const MAX_DURATION_MINUTES = 8 * 60;
const MAX_RECUR_DAYS_OUT = 42; // 6 weeks

function sixWeeksFrom(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + MAX_RECUR_DAYS_OUT);
  return toLocalDateStr(d);
}

function quickDateOptions() {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const at = daysAhead => { const d = new Date(today); d.setDate(today.getDate() + daysAhead); return toLocalDateStr(d); };
  return [
    { label: 'Today', value: at(0) },
    { label: 'Tomorrow', value: at(1) },
    { label: 'Sat', value: at((6 - today.getDay() + 7) % 7) },
    { label: 'Sun', value: at((7 - today.getDay()) % 7) },
  ];
}

function joinNames(names) {
  if (!names.length) return '';
  return names.length > 1 ? `${names.slice(0, -1).join(', ')} & ${names.slice(-1)}` : names[0];
}

export default function ScheduleSitter({ onBack }) {
  const { familyData, currentUser } = useApp() || {};
  const sitters      = (familyData?.sitters || []).filter(s => s.active !== false);
  const carePartners = familyData?.carePartners || [];
  const children      = familyData?.children || [];
  const activityTypes = resolveActivityTypes(familyData?.config);

  const [form, setForm] = useState(() => ({
    mode: 'sitter',            // 'helper' | 'sitter'
    activityType: null,
    sitterId: '',
    kidIds: children.map(c => c.id),
    date: '', startTime: '16:00', durationMinutes: 180,
    isRecurring: false,
    recurDays: [],
    recurUntil: '',
    note: '', noteAutoGenerated: true,
    whosContacting: '',
  }));
  const [saving, setSaving]     = useState(false);
  const [saved,  setSaved]      = useState(false);
  const [error,  setError]      = useState(null);
  const [blackoutWarning, setBlackoutWarning] = useState(false);
  const [showCustomDate, setShowCustomDate]   = useState(false);
  const [showCustomTime, setShowCustomTime]   = useState(false);
  const [recurringResult, setRecurringResult] = useState(null); // { total, failures }

  // Derive end time in 24h "HH:MM" — the only form safe to splice into an
  // ISO datetime or store alongside startTime (also 24h).
  function calcEndTime24h(startTime, mins) {
    if (!startTime) return '';
    const [h, m] = startTime.split(':').map(Number);
    const total = (h * 60 + m + mins) % (24 * 60);
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  }

  function checkBlackout(sitterId, dateStr) {
    if (!sitterId || !dateStr) { setBlackoutWarning(false); return; }
    const sitter = sitters.find(s => s.id === sitterId);
    if (!sitter?.blackoutDays?.length) { setBlackoutWarning(false); return; }
    const dow = new Date(dateStr + 'T12:00:00').getDay();
    setBlackoutWarning(sitter.blackoutDays.includes(RECUR_DAYS[dow]));
  }

  function handleSitterChange(sitterId) {
    setForm(f => ({ ...f, sitterId }));
    checkBlackout(sitterId, form.date);
  }
  function handleDateChange(date) {
    setForm(f => ({ ...f, date }));
    checkBlackout(form.sitterId, date);
  }

  function autoNoteFor(kidIds) {
    const names = children.filter(c => kidIds.includes(c.id)).map(c => c.firstName).filter(Boolean);
    return names.length ? `Primarily hangout with ${joinNames(names)} and facilitate age-appropriate activities` : '';
  }

  function toggleKid(id) {
    setForm(f => {
      const kidIds = f.kidIds.includes(id) ? f.kidIds.filter(x => x !== id) : [...f.kidIds, id];
      return { ...f, kidIds, note: f.noteAutoGenerated ? autoNoteFor(kidIds) : f.note };
    });
  }

  function setActivityType(id) {
    setForm(f => {
      const activityType = f.activityType === id ? null : id;
      const kidIds = activityType === 'dogs_only' ? [] : f.kidIds;
      return { ...f, activityType, kidIds, note: f.noteAutoGenerated ? autoNoteFor(kidIds) : f.note };
    });
  }

  function toggleRecurDay(code) {
    setForm(f => ({
      ...f,
      recurDays: f.recurDays.includes(code) ? f.recurDays.filter(x => x !== code) : [...f.recurDays, code],
    }));
  }

  // One kid selected → that kid's own calendar (matched by name via Settings'
  // calendar assignments, same lookup LogisticsModule uses for "theirs"
  // matching). Zero or multiple kids → the shared family calendar, since a
  // single kid's calendar shouldn't carry a session that isn't just theirs.
  function resolveCalendarId(kidIds) {
    const fallback = familyData?.config?.primaryCalendarId || 'primary';
    if (kidIds.length !== 1) return fallback;
    const kid = children.find(c => c.id === kidIds[0]);
    if (!kid?.firstName) return fallback;
    const calAssignments = familyData?.config?.calendarAssignments || {};
    const match = Object.entries(calAssignments)
      .find(([, name]) => name?.toLowerCase() === kid.firstName.toLowerCase());
    return match ? match[0] : fallback;
  }

  const sitter    = sitters.find(s => s.id === form.sitterId);
  const isUnpaid  = sitter?.rateOverride === 0;
  const estimate  = sitter && !isUnpaid
    ? calculateSessionPay(form.durationMinutes, form.kidIds.length, familyData?.config, sitter.rateOverride ?? null)
    : null;

  const canSubmit = !!(form.sitterId && form.date && form.startTime && (!form.isRecurring || form.recurDays.length > 0));

  async function scheduleOneOff() {
    const sessionId = `sess_${form.sitterId}_${form.date}_${Date.now()}`;
    const endTime   = calcEndTime24h(form.startTime, form.durationMinutes);
    const calId     = resolveCalendarId(form.kidIds);
    const tz        = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const title     = `${form.mode === 'helper' ? 'Helper' : 'Sitter'} — ${sitter.firstName}`;

    const evt = await createEvent(calId, {
      summary:     title,
      // Defaults to a reminder, not assigned to the caregiver — the session
      // doc (below) already records who's booked; the calendar block itself
      // shouldn't need review/assignment on Review & Assign.
      description: buildFOHDescription(form.note || '', { reminder: true, reviewed: true, session: sessionId }),
      start: { dateTime: `${form.date}T${form.startTime}:00`, timeZone: tz },
      end:   { dateTime: `${form.date}T${endTime}:00`,        timeZone: tz },
    });

    await fsSet(`families/${familyData.id}/sessions/${sessionId}`, {
      sitterId: form.sitterId,
      sitterName: `${sitter.firstName || ''} ${sitter.lastName || ''}`.trim(),
      mode: form.mode, activityType: form.activityType, kidIds: form.kidIds,
      date: form.date, startTime: form.startTime, endTime, durationMinutes: form.durationMinutes,
      isRecurring: false, bookingGroupId: null, recurrenceRule: null,
      rateOverride: sitter.rateOverride ?? null, estimatedPay: isUnpaid ? 0 : estimate,
      note: form.note, contactedBy: form.whosContacting,
      gcalCalendarId: calId, gcalEventId: evt.id,
      status: SESSION_STATUS.SCHEDULED, declinedAt: null, declinedReason: null,
      createdAt: new Date().toISOString(), createdBy: currentUser?.email,
    });

    await fsLogChange(familyData.id, {
      actorId: currentUser?.email, actorName: currentUser?.name,
      module: 'logistics', action: 'schedule_sitter', entityType: 'session',
      toValue: `${sitter.firstName} on ${form.date}`,
    });
  }

  async function scheduleRecurring() {
    const calId    = resolveCalendarId(form.kidIds);
    const tz       = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const title    = `${form.mode === 'helper' ? 'Helper' : 'Sitter'} — ${sitter.firstName}`;
    const endTime  = calcEndTime24h(form.startTime, form.durationMinutes);
    const until    = form.recurUntil && form.recurUntil <= sixWeeksFrom(form.date) ? form.recurUntil : sixWeeksFrom(form.date);
    const byDay    = form.recurDays.map(c => c.toUpperCase()).join(',');
    const untilStamp = `${until.replace(/-/g, '')}T235959Z`;
    const bookingGroupId = `book_${Date.now()}`;

    // One native recurring master — Google's own RRULE expansion, so the
    // family's calendar shows one clean recurring entry, not N separate ones.
    const master = await createEvent(calId, {
      summary:     title,
      description: buildFOHDescription(form.note || '', { reminder: true, reviewed: true }),
      start: { dateTime: `${form.date}T${form.startTime}:00`, timeZone: tz },
      end:   { dateTime: `${form.date}T${endTime}:00`,        timeZone: tz },
      recurrence: [`RRULE:FREQ=WEEKLY;BYDAY=${byDay};UNTIL=${untilStamp}`],
    });

    // Enumerate the concrete instances Google generated — same technique
    // RecurringDefaults.jsx uses for existing series (fetchEvents always
    // expands recurring events via singleEvents:true).
    const windowStart = new Date(`${form.date}T00:00:00`);
    const windowEnd   = new Date(`${until}T23:59:59`);
    const instances = await fetchEvents({
      calendarId: calId,
      timeMin: windowStart.toISOString(),
      timeMax: windowEnd.toISOString(),
    });
    const mine = instances.filter(e => e.recurringEventId === master.id);

    // One session doc + one per-instance FOH patch per occurrence,
    // sequential (not Promise.all) to avoid a GCal burst and ambiguous
    // partial failures.
    const failures = [];
    for (const inst of mine) {
      const dateStr = (inst.start?.dateTime || inst.start?.date || '').split('T')[0];
      if (!dateStr) continue;
      const sessionId = `sess_${form.sitterId}_${dateStr}_${bookingGroupId}`;
      try {
        await patchEvent(calId, inst.id, {
          description: buildFOHDescription(inst.description || '', { reminder: true, reviewed: true, session: sessionId }),
        });
        await fsSet(`families/${familyData.id}/sessions/${sessionId}`, {
          sitterId: form.sitterId,
          sitterName: `${sitter.firstName || ''} ${sitter.lastName || ''}`.trim(),
          mode: form.mode, activityType: form.activityType, kidIds: form.kidIds,
          date: dateStr, startTime: form.startTime, endTime, durationMinutes: form.durationMinutes,
          isRecurring: true, bookingGroupId, recurrenceRule: { byDay: form.recurDays, until },
          rateOverride: sitter.rateOverride ?? null, estimatedPay: isUnpaid ? 0 : estimate,
          note: form.note, contactedBy: form.whosContacting,
          gcalCalendarId: calId, gcalEventId: inst.id,
          status: SESSION_STATUS.SCHEDULED, declinedAt: null, declinedReason: null,
          createdAt: new Date().toISOString(), createdBy: currentUser?.email,
        });
      } catch (err) {
        failures.push(dateStr);
      }
    }

    await fsLogChange(familyData.id, {
      actorId: currentUser?.email, actorName: currentUser?.name,
      module: 'logistics', action: 'schedule_sitter_recurring', entityType: 'session',
      toValue: `${sitter.firstName} — ${form.recurDays.join(',')} through ${until} (${mine.length - failures.length} sessions)`,
    });

    if (failures.length) setRecurringResult({ total: mine.length, failures });
  }

  async function handleSchedule() {
    if (!canSubmit) {
      setError(`Please fill in caregiver, date, and start time${form.isRecurring ? ', and at least one repeat day' : ''}.`);
      return;
    }
    if (!familyData?.id) { setError('Family data not loaded.'); return; }
    setSaving(true); setError(null);
    try {
      if (form.isRecurring) await scheduleRecurring();
      else await scheduleOneOff();
      setSaved(true);
    } catch (err) {
      setError('Could not save: ' + err.message);
    }
    setSaving(false);
  }

  if (saved) return (
    <div className="module-screen">
      <div className="module-header">
        <button className="btn btn--back" onClick={onBack}>← Schedule</button>
        <h1>Schedule Caregiver</h1>
      </div>
      <div className="form-body" style={{ alignItems: 'center', paddingTop: 48, gap: 16 }}>
        <div style={{ fontSize: 40 }}>🌱</div>
        <h2 style={{ color: 'var(--forest-dark)' }}>{form.isRecurring ? 'Recurring booking scheduled' : 'Caregiver scheduled'}</h2>
        <p style={{ color: 'var(--sage)', textAlign: 'center' }}>
          {form.isRecurring ? 'Calendar series created and sessions saved.' : 'Calendar event created and session saved.'}
        </p>
        {recurringResult?.failures?.length > 0 && (
          <p style={{ color: 'var(--amber)', textAlign: 'center', fontSize: 13 }}>
            Saved, but {recurringResult.failures.length} date{recurringResult.failures.length !== 1 ? 's' : ''} didn't sync fully: {recurringResult.failures.join(', ')}
          </p>
        )}
        <button className="btn btn--primary" onClick={onBack}>Done</button>
      </div>
    </div>
  );

  return (
    <div className="module-screen">
      <div className="module-header">
        <button className="btn btn--back" onClick={onBack}>← Schedule</button>
        <h1>Schedule Caregiver</h1>
      </div>
      <div className="travel-form">
        {error && <div className="warning-banner">{error}</div>}

        {/* ── Section 1: Caregiver ── */}
        <div className="form-section">
          <div className="form-section__title">Caregiver</div>
          {sitters.length === 0 ? (
            <p className="settings-note" style={{ marginTop: 8 }}>No caregivers added yet — add them in Settings → Caregivers & Access.</p>
          ) : (
            <div className="traveler-pills">
              {sitters.map(s => (
                <button key={s.id} type="button"
                  className={`traveler-pill ${form.sitterId === s.id ? 'active' : ''}`}
                  onClick={() => handleSitterChange(s.id)}>
                  {s.firstName}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Section 2: When (Helper/Sitter) ── */}
        <div className="form-section">
          <div className="form-section__title">When</div>
          <div className="traveler-pills">
            <button type="button" className={`traveler-pill ${form.mode === 'helper' ? 'active' : ''}`}
              onClick={() => setForm(f => ({ ...f, mode: 'helper' }))}>🏡 Helper — I'm home</button>
            <button type="button" className={`traveler-pill ${form.mode === 'sitter' ? 'active' : ''}`}
              onClick={() => setForm(f => ({ ...f, mode: 'sitter' }))}>🚪 Sitter — we're out</button>
          </div>
        </div>

        {/* ── Section 3: What (optional activity) ── */}
        <div className="form-section">
          <div className="form-section__title">What <span style={{ fontWeight: 400, color: 'var(--sage)' }}>(optional)</span></div>
          <div className="traveler-pills">
            {activityTypes.map(a => (
              <button key={a.id} type="button" className={`traveler-pill ${form.activityType === a.id ? 'active' : ''}`}
                onClick={() => setActivityType(a.id)}>{a.label}</button>
            ))}
          </div>
        </div>

        {/* ── Section 4: Kids (hidden for Dogs Only) ── */}
        {form.activityType !== 'dogs_only' && children.length > 0 && (
          <div className="form-section">
            <div className="form-section__title">Kids</div>
            <div className="pickup-cp-pills">
              {children.map(c => (
                <button key={c.id} type="button" className={`pickup-cp-pill ${form.kidIds.includes(c.id) ? 'active' : ''}`}
                  onClick={() => toggleKid(c.id)}>{c.firstName}</button>
              ))}
            </div>
          </div>
        )}

        {/* ── Section 5: Date & Time ── */}
        <div className="form-section">
          <div className="form-section__title">Date &amp; Time</div>
          <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginBottom: 10 }}>
            <input type="checkbox" checked={form.isRecurring}
              onChange={e => setForm(f => ({ ...f, isRecurring: e.target.checked }))} />
            Recurring (repeats weekly)
          </label>

          {!form.isRecurring ? (
            <>
              <label className="form-label">Date</label>
              <div className="traveler-pills">
                {quickDateOptions().map(o => (
                  <button key={o.label} type="button"
                    className={`traveler-pill ${form.date === o.value && !showCustomDate ? 'active' : ''}`}
                    onClick={() => { handleDateChange(o.value); setShowCustomDate(false); }}>{o.label}</button>
                ))}
                <button type="button" className={`traveler-pill ${showCustomDate ? 'active' : ''}`}
                  onClick={() => setShowCustomDate(true)}>Other</button>
              </div>
              {showCustomDate && (
                <input type="date" className="input" style={{ marginTop: 8 }} value={form.date}
                  onChange={e => handleDateChange(e.target.value)} />
              )}
            </>
          ) : (
            <>
              <label className="form-label">Starts</label>
              <input type="date" className="input" value={form.date}
                onChange={e => handleDateChange(e.target.value)} />
              <label className="form-label" style={{ marginTop: 10 }}>Repeats on</label>
              <div className="pickup-days-row">
                {RECUR_DAYS.map(d => (
                  <button key={d} type="button" className={`pickup-day-btn ${form.recurDays.includes(d) ? 'active' : ''}`}
                    onClick={() => toggleRecurDay(d)}>{d}</button>
                ))}
              </div>
              <label className="form-label" style={{ marginTop: 10 }}>Ends</label>
              <input type="date" className="input" value={form.recurUntil}
                max={form.date ? sixWeeksFrom(form.date) : undefined}
                onChange={e => setForm(f => ({ ...f, recurUntil: e.target.value }))} />
              <p className="settings-note" style={{ marginTop: 4 }}>Up to 6 weeks out. Reopen this form later to extend further.</p>
            </>
          )}

          <label className="form-label" style={{ marginTop: 10 }}>Start time</label>
          <div className="traveler-pills">
            {QUICK_TIMES.map(o => (
              <button key={o.label} type="button"
                className={`traveler-pill ${form.startTime === o.value && !showCustomTime ? 'active' : ''}`}
                onClick={() => { setForm(f => ({ ...f, startTime: o.value })); setShowCustomTime(false); }}>{o.label}</button>
            ))}
            <button type="button" className={`traveler-pill ${showCustomTime ? 'active' : ''}`}
              onClick={() => setShowCustomTime(true)}>Other</button>
          </div>
          {showCustomTime && (
            <input type="time" className="input" style={{ marginTop: 8 }} value={form.startTime}
              onChange={e => setForm(f => ({ ...f, startTime: e.target.value }))} />
          )}

          <label className="form-label" style={{ marginTop: 10 }}>Duration</label>
          <div className="duration-control">
            <button className="btn btn--icon" type="button"
              onClick={() => setForm(f => ({ ...f, durationMinutes: Math.max(15, f.durationMinutes - 15) }))}>−</button>
            <span>{Math.floor(form.durationMinutes / 60)}h {form.durationMinutes % 60 ? `${form.durationMinutes % 60}m` : ''}</span>
            <button className="btn btn--icon" type="button"
              onClick={() => setForm(f => ({ ...f, durationMinutes: Math.min(MAX_DURATION_MINUTES, f.durationMinutes + 15) }))}>+</button>
          </div>
          {form.startTime && (
            <p className="settings-note" style={{ marginTop: 6 }}>
              Ends at {fmtClockTime(calcEndTime24h(form.startTime, form.durationMinutes))}
            </p>
          )}
          {sitter && (
            <p className="settings-note" style={{ marginTop: 6 }}>
              {isUnpaid ? 'Unpaid — family' : estimate != null ? `Est. $${estimate}` : ''}
            </p>
          )}
          {blackoutWarning && (
            <div className="warning-banner" style={{ marginTop: 10 }}>🌾 This caregiver is typically unavailable on this day.</div>
          )}
        </div>

        {/* ── Section 6: Coverage ── */}
        <div className="form-section">
          <div className="form-section__title">Coverage</div>
          <label className="form-label">Who's contacting the caregiver?</label>
          <div className="traveler-pills">
            {carePartners.map(cp => (
              <button key={cp.identifier} type="button"
                className={`traveler-pill ${form.whosContacting === cp.identifier ? 'active' : ''}`}
                onClick={() => setForm(f => ({ ...f, whosContacting: cp.identifier }))}>
                {cp.name?.split(' ')[0] || cp.name}
              </button>
            ))}
          </div>
          <label className="form-label">Note for caregiver (optional)</label>
          <textarea className="input input--textarea" rows={2} value={form.note}
            placeholder="e.g. Kids have swim at 4pm, dinner is in the fridge"
            onChange={e => setForm(f => ({ ...f, note: e.target.value, noteAutoGenerated: false }))} />
        </div>

        <button className="btn btn--primary btn--full"
          disabled={saving || !canSubmit}
          onClick={handleSchedule}
          style={{ marginTop: 4 }}>
          {saving ? 'Saving…' : '🌱 Schedule & Add to Calendar'}
        </button>
      </div>
    </div>
  );
}
